import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { EASE } from "@/components/bits";
import { PRODUCT } from "@/config/product";
import { PREORDER_COLOUR_IDS } from "@/config/pogoConfig";
import { trackBoth } from "@/lib/meta";
import { clarityEvent } from "@/lib/clarity";

// Mobile sticky purchase bar. Product page: appears once the main ADD TO
// CART scrolls out of view — shows the selected pair and adds it to the
// bag (or scrolls back if nothing's picked). Landing: links to /product.
export default function StickyBuyBar({ colour, size, onPreorder, onNeedSize }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isProduct = pathname === "/product";
  const [past, setPast] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(true);

  useEffect(() => {
    const onScroll = () =>
      setPast(window.scrollY > window.innerHeight * 0.5);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    let obs;
    if (isProduct) {
      const cta = document.querySelector('[data-testid="buy-preorder-button"]');
      if (cta) {
        obs = new IntersectionObserver(([e]) => setCtaVisible(e.isIntersecting), {
          threshold: 0,
        });
        obs.observe(cta);
      }
    }
    return () => {
      window.removeEventListener("scroll", onScroll);
      obs?.disconnect();
    };
  }, [isProduct]);

  const show = past && (!isProduct || !ctaVisible);
  const ready = colour && size;
  const notDropped = isProduct && colour && !PREORDER_COLOUR_IDS.includes(colour.id);

  const act = () => {
    trackBoth("CtaClicked", { origin: "sticky_bar" });
    if (isProduct) clarityEvent("StickyPreorderClicked");
    if (!isProduct) {
      navigate("/product");
      return;
    }
    // Colourway not in this drop — take them straight to the notify form.
    if (notDropped) {
      document
        .getElementById("colour-drop-banner")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(
        () => document.getElementById("drop-notify-email")?.focus(),
        450
      );
      return;
    }
    if (ready) {
      onPreorder?.();
    } else if (colour) {
      // Pink picked but no size — open the bag slide-out with a size picker.
      onNeedSize?.();
    } else {
      document.getElementById("buy")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const thumb = PRODUCT.colourImages?.[colour?.id] || PRODUCT.images[0].src;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="sticky-buy-bar"
          className="sm:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3"
          initial={{ y: 96 }}
          animate={{ y: 0 }}
          exit={{ y: 96 }}
          transition={{ duration: 0.3, ease: EASE }}
        >
          <div className="rounded-full border-2 border-ink bg-ivory shadow-[0_8px_24px_rgba(17,17,17,0.18)] flex items-center gap-2.5 pl-1.5 pr-1.5 py-1.5">
            <img
              data-testid="sticky-thumb"
              src={thumb}
              alt=""
              className="w-10 h-10 rounded-full object-cover border-2 border-ink shrink-0"
              draggable="false"
            />
            <div className="flex-1 min-w-0">
              <p className="font-display uppercase text-xs leading-none truncate">
                {PRODUCT.title}
              </p>
              <p data-testid="sticky-variant" className="text-[11px] text-ink/50 mt-1 truncate">
                {ready
                  ? `${colour.name} · ${size}`
                  : colour
                  ? colour.name
                  : `$${PRODUCT.price} ${PRODUCT.currency}`}
              </p>
            </div>
            <button
              data-testid="sticky-preorder-button"
              onClick={act}
              className="min-h-[44px] px-4 rounded-full bg-[#FF6BAA] text-ink font-display text-xs uppercase tracking-wide transition-transform duration-150 active:scale-95 shrink-0"
            >
              {notDropped
                ? "Notify me"
                : ready
                ? `Add to cart — $${PRODUCT.price}`
                : "Preorder"}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
